// ** React Imports

import Box, { BoxProps } from '@mui/material/Box'
// ** MUI Imports
import Chip from '@mui/material/Chip'
import ListItem from '@mui/material/ListItem'
import ListItemButton, { ListItemButtonProps } from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import { styled, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { ElementType, memo } from 'react'
// ** React Router Imports
import { Link, useLocation } from 'react-router-dom'
// ** Custom Components Imports
import Icon from 'src/@core/components/icon/index'

import { Settings } from 'src/@core/context/settingsContext'
// ** Types
import { NavGroup, NavLink } from 'src/@core/layouts/types'
// ** Util Imports
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
// ** Configs Import
import themeConfig from 'src/configs/themeConfig'
import Translations from 'src/layouts/components/Translations'

interface Props {
  parent?: boolean
  item: NavLink
  navHover?: boolean
  settings: Settings
  navVisible?: boolean
  collapsedNavWidth: number
  navigationBorderWidth: number
  toggleNavVisibility: () => void
  isSubToSub?: NavGroup | undefined
}

// ** Styled Components
const MenuNavLink = styled(ListItemButton)<
  ListItemButtonProps & {
    component?: ElementType
    to: string
    target?: '_blank' | undefined
  }
>(({ theme }) => ({
  width: '100%',
  marginLeft: theme.spacing(3.5),
  marginRight: theme.spacing(3.5),
  borderRadius: theme.shape.borderRadius,
  transition: 'padding-left .25s ease-in-out, padding-right .25s ease-in-out',
  '&.active': {
    '&, &:hover': {
      boxShadow: `0px 2px 6px ${hexToRGBA(theme.palette.primary.main, 0.48)}`,
      background: `linear-gradient(72.47deg, ${
        theme.direction === 'ltr' ? theme.palette.primary.main : hexToRGBA(theme.palette.primary.main, 0.7)
      } 22.16%, ${
        theme.direction === 'ltr' ? hexToRGBA(theme.palette.primary.main, 0.7) : theme.palette.primary.main
      } 76.47%)`,
      '&.Mui-focusVisible': {
        background: `linear-gradient(72.47deg, ${theme.palette.primary.dark} 22.16%, ${hexToRGBA(
          theme.palette.primary.dark,
          0.7
        )} 76.47%)`
      }
    },
    '& .MuiTypography-root, & svg': {
      color: `${theme.palette.common.white} !important`
    }
  }
}))

const MenuItemTextMetaWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  transition: 'opacity .25s ease-in-out',
  ...(themeConfig.menuTextTruncate && { overflow: 'hidden' })
}))

const VerticalNavLink = ({
  item,
  parent,
  navHover,
  settings,
  navVisible,
  isSubToSub,
  collapsedNavWidth,
  toggleNavVisibility,
  navigationBorderWidth
}: Props) => {
  // ** Hooks
  const theme = useTheme()
  const location = useLocation()

  const currentPathname = window.location.pathname

  // ** Vars
  const { mode, navCollapsed } = settings

  const icon = parent && !item.icon ? themeConfig.navSubItemIcon : item.icon

  const conditionalColors = () => {
    if (mode === 'semi-dark') {
      return {
        '&:hover': {
          backgroundColor: `rgba(${theme.palette.customColors.dark}, 0.04)`
        },
        '& .MuiTypography-root, & svg': {
          color: `rgba(${theme.palette.customColors.dark}, 0.6)`
        }
      }
    } else
      return {
        '& .MuiTypography-root, & svg': {
          color: 'text.secondary'
        }
      }
  }

  const isNavLinkActive = () => {
    const path = item.path ?? ''
    const normalizedPath = path.startsWith('/') ? path : `/${path}`

    if (location.pathname.startsWith(normalizedPath)) {
      const currentPathSegments = location.pathname.split('/').filter(Boolean)
      const itemPathSegments = normalizedPath.split('/').filter(Boolean)

      if (itemPathSegments.every(segment => currentPathSegments.includes(segment))) {
        return true
      }
    }

    return location.pathname === normalizedPath
  }

  const normalizeHref = (path: string): string => {
    if (path.startsWith('/')) return path
    return `/${path}`
  }

  return (
    <ListItem disablePadding className='nav-link' disabled={item.disabled || false} sx={{ mt: 1, px: '0 !important' }}>
      <MenuNavLink
        component={Link}
        {...(item.disabled && { tabIndex: -1 })}
        className={isNavLinkActive() ? 'active' : ''}
        to={item.path === undefined ? '/' : normalizeHref(item.path)}
        {...(item.openInNewTab ? { target: '_blank' } : null)}
        onClick={e => {
          if (item.path === undefined) {
            e.preventDefault()
            e.stopPropagation()
          }
          if (navVisible) {
            toggleNavVisibility()
          }
          // reset page when switch menu
          sessionStorage.removeItem(`currentPage+menu=${currentPathname}`)
        }}
        sx={{
          py: 2,
          ...conditionalColors(),
          ...(item.disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' }),
          px: navCollapsed && !navHover ? (collapsedNavWidth - navigationBorderWidth - 22 - 28) / 8 : 4
        }}
      >
        <ListItemIcon
          sx={{
            transition: 'margin .25s ease-in-out',
            ...(navCollapsed && !navHover ? { mr: 0 } : { mr: 2 }),
            ...(parent ? { ml: 1.5, mr: 3.5 } : {}),
            '& svg': {
              fontSize: '1.25rem',
              ...(!parent ? { fontSize: '1.375rem' } : {}),
              ...(parent && item.icon ? { fontSize: '1.25rem' } : {})
            }
          }}
        >
          <Icon icon={icon as string} fontSize='1.25rem' />
        </ListItemIcon>

        <MenuItemTextMetaWrapper
          sx={{
            ...(isSubToSub ? { ml: 2 } : {}),
            ...(navCollapsed && !navHover ? { opacity: 0 } : { opacity: 1 })
          }}
        >
          <Typography
            {...((themeConfig.menuTextTruncate || (!themeConfig.menuTextTruncate && navCollapsed && !navHover)) && {
              noWrap: true
            })}
          >
            <Translations text={item.title} />
          </Typography>
          {item.badgeContent ? (
            <Chip
              label={item.badgeContent}
              color={item.badgeColor || 'primary'}
              sx={{
                height: 20,
                fontWeight: 500,
                '& .MuiChip-label': { px: 1.5, textTransform: 'capitalize' }
              }}
            />
          ) : null}
        </MenuItemTextMetaWrapper>
      </MenuNavLink>
    </ListItem>
  )
}

export default memo(VerticalNavLink, (prev, next) => {
  return (
    prev.item === next.item &&
    prev.parent === next.parent &&
    prev.navHover === next.navHover &&
    prev.navVisible === next.navVisible &&
    prev.collapsedNavWidth === next.collapsedNavWidth &&
    prev.navigationBorderWidth === next.navigationBorderWidth &&
    prev.isSubToSub === next.isSubToSub &&
    prev.settings?.mode === next.settings?.mode &&
    prev.settings?.navCollapsed === next.settings?.navCollapsed
  )
})
